import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  ActiveAlertRulesTable,
  AlertRuleDialog,
  ApprovalQueue,
  ProposeNewButton,
  type DialogTarget,
} from "../components/AlertRules";
import { PageHeader } from "../components/PageHeader";
import { SubStepShell } from "../components/StepShell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { useAlertRulesStore } from "../lib/api/alert-rules";
import { useOfficeMapping } from "../lib/api/office-mapping";

export function ManageAlerts() {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const store = useAlertRulesStore();
  const { data: offices } = useOfficeMapping();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState<DialogTarget | null>(null);
  const [dialogKey, setDialogKey] = useState(0);

  const backTo =
    from === "exceptions-log" ? "/underwriting-exceptions/exceptions-log" : "/underwriting-exceptions";
  const backLabel =
    from === "exceptions-log" ? "Back to Deal-Level Exceptions Log" : "Back to Underwriting Health";

  function openDialog(next: DialogTarget) {
    setTarget(next);
    setDialogKey((k) => k + 1);
    setDialogOpen(true);
  }

  return (
    <SubStepShell subStepId="manage-alerts" backTo={backTo} backLabel={backLabel}>
      <PageHeader
        title="Manage Alerts"
        subtitle="Configure which policy flags trigger a notification, and who receives it. Every change goes through review before it takes effect."
      />

      <Card className="border-border">
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base font-semibold">Active Alert Rules</CardTitle>
            <CardDescription>
              What is being watched today, at what threshold, and who hears about it.
            </CardDescription>
          </div>
          <ProposeNewButton onClick={() => openDialog({ rule: null })} />
        </CardHeader>
        <CardContent>
          <ActiveAlertRulesTable
            rules={store.activeRules}
            onProposeChange={(rule) => openDialog({ rule })}
          />
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Approval Queue</CardTitle>
          <CardDescription>
            Proposed alert rules and threshold changes awaiting review.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ApprovalQueue proposals={store.proposals} onReview={store.review} />
        </CardContent>
      </Card>

      <p className="text-xs leading-relaxed text-muted-foreground">
        Superset is the engine behind these rules — this page is the configuration surface. Changes
        take effect on the next scheduled sync once approved.
      </p>

      <AlertRuleDialog
        key={dialogKey}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        target={target}
        offices={offices ?? []}
        onSubmit={store.propose}
      />
    </SubStepShell>
  );
}
